import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import api from '../services/api';
import { trackPaymentMethodSelected, trackCartEvent, trackSilentFailure, trackOrderEvent } from '../services/analytics';
import { Button, Card } from '../components/ui';

const PaymentCard = ({ title, description, icon, active, onClick }) => (
  <div 
    onClick={onClick}
    className={`
      p-[18px] rounded-[16px] border-[2px] cursor-pointer transition-all duration-200
      flex items-center gap-[14px]
      ${active ? 'border-brand-accent-red bg-[#FFF9F9] dark:bg-brand-accent-red/10' : 'border-light-border-default dark:border-brand-border-default bg-white dark:bg-brand-surface hover:border-light-border-hover dark:hover:border-brand-border-hover'}
    `}
  >
    <div className={`
      w-[44px] h-[44px] rounded-full flex items-center justify-center flex-shrink-0
      ${active ? 'bg-brand-accent-red text-white' : 'bg-light-secondary dark:bg-brand-elevated text-light-text-secondary dark:text-brand-text-secondary'}
    `}>
      {icon}
    </div>
    <div className="flex flex-col flex-grow text-left">
      <span className={`font-bold text-[15px] ${active ? 'text-brand-accent-red' : 'text-light-text-primary dark:text-brand-text-primary'}`}>
        {title}
      </span>
      {description && (
        <span className="text-[13px] text-light-text-secondary dark:text-brand-text-secondary mt-[2px]">
          {description}
        </span>
      )}
    </div>
    <div className={`
      w-[22px] h-[22px] rounded-full border-[2px] flex items-center justify-center flex-shrink-0
      ${active ? 'border-brand-accent-red bg-brand-accent-red' : 'border-light-border-default dark:border-brand-border-default'}
    `}>
      {active && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      )}
    </div>
  </div>
);

const Payment = () => {
  const {
    state: { items },
    dispatch,
  } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [draftOrder, setDraftOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('mobile_money');
  const [transactionState, setTransactionState] = useState('idle'); // idle | processing | success
  const [viewportHeight, setViewportHeight] = useState(0);

  useEffect(() => {
    const updateHeight = () => {
      const vh = window.visualViewport?.height || window.innerHeight;
      setViewportHeight(vh);
    };
    
    updateHeight();
    window.addEventListener('resize', updateHeight);
    window.visualViewport?.addEventListener('resize', updateHeight);
    
    return () => {
      window.removeEventListener('resize', updateHeight);
      window.visualViewport?.removeEventListener('resize', updateHeight);
    };
  }, []);

  const deliveryInfo = location.state?.deliveryInfo || location.state?.delivery;
  const total = items.reduce(
    (sum, i) => sum + i.product.price * i.qty,
    0
  );

  // Calculate 75% of viewport height minus header
  const headerHeight = 80;
  const contentHeight = viewportHeight > 0 ? Math.floor(viewportHeight * 0.75) : 600;
  const availableHeight = Math.max(contentHeight - headerHeight, 400);

  useEffect(() => {
    if (items.length === 0 && !draftOrder) {
      navigate('/cart');
    }

    if (!deliveryInfo && !location.state?.fromProofUpload && !draftOrder) {
      navigate('/delivery');
    }
  }, [items, deliveryInfo, navigate, draftOrder, location.state]);

  const ensureDraftOrder = async (paymentMethod) => {
    if (draftOrder?.id) {
      return draftOrder;
    }

    const draftPayload = {
      items: items.map(({ product, qty }) => ({
        productId: product._id ?? product.id,
        quantity: qty,
        price: product.price,
      })),
      totalAmount: total,
      delivery: deliveryInfo,
      paymentMethod,
    };

    const response = await api.post('/orders/draft', draftPayload);
    const createdDraft = response.data;

    if (createdDraft.orderToken) {
      localStorage.setItem('activeOrderToken', createdDraft.orderToken);
      localStorage.setItem(`orderToken:${createdDraft.id}`, createdDraft.orderToken);
    }

    setDraftOrder(createdDraft);
    return createdDraft;
  };

  const processPayment = async () => {
    setLoading(true);
    setError(null);
    setTransactionState('processing');

    trackCartEvent({
      action: 'checkout',
      status: 'started',
      cartValue: total,
      metadata: { method: selectedMethod }
    });

    try {
      // Mock animation delay
      await new Promise(resolve => setTimeout(resolve, 1500));
      setTransactionState('success');
      await new Promise(resolve => setTimeout(resolve, 800)); // Show tick before redirect

      const draft = await ensureDraftOrder(selectedMethod);
      const orderToken =
        localStorage.getItem('activeOrderToken') ||
        localStorage.getItem(`orderToken:${draft.id}`);

      const headers = orderToken ? { 'x-order-token': orderToken } : {};

      await api.patch(
        `/orders/${draft.id}/progress`,
        {
          paymentMethod: selectedMethod,
          delivery: deliveryInfo,
        },
        { headers }
      );

      if (selectedMethod === 'mobile_money' || selectedMethod === 'bank_transfer') {
        const ua =
          typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          ua
        );

        if (isMobile && selectedMethod === 'mobile_money') {
          window.location.href = 'tel:*185%23';
        }

        trackCartEvent({
          action: 'checkout',
          status: 'succeeded',
          cartValue: total,
          metadata: { nextStep: 'upload_proof', method: selectedMethod }
        });

        navigate(`/upload-proof/${draft.id}`, {
          state: { paymentMethod: selectedMethod, orderToken },
        });
        return;
      }

      if (selectedMethod === 'cash_on_delivery') {
        trackOrderEvent({
          action: 'placement',
          status: 'started',
          orderId: draft.id,
          metadata: { method: selectedMethod }
        });

        const res = await api.post(
          `/orders/${draft.id}/submit`,
          {},
          { headers }
        );
        const id = res.data?.id || res.data?._id;

        if (!id) {
          trackOrderEvent({
            action: 'placement',
            status: 'failed',
            orderId: draft.id,
            errorMessage: 'Missing ID in response',
          });
          trackSilentFailure({
            context: 'order_submit',
            expectedState: 'id_returned',
            actualState: 'missing_id',
          });
          throw new Error('Order submission failed. Please try again.');
        }

        trackOrderEvent({
          action: 'placement',
          status: 'succeeded',
          orderId: id,
          metadata: { method: selectedMethod }
        });

        trackCartEvent({
          action: 'checkout',
          status: 'succeeded',
          cartValue: total,
          metadata: { nextStep: 'confirmation', method: selectedMethod }
        });

        if (orderToken) {
          localStorage.setItem(`orderToken:${id}`, orderToken);
        }

        localStorage.removeItem('activeOrderToken');
        dispatch({ type: 'CLEAR' });

        navigate(`/confirmation/${id}`, {
          state: { paymentMethod: selectedMethod, orderToken },
        });
        return;
      }
    } catch (err) {
      console.error('Payment error:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to process payment';
      
      trackCartEvent({
        action: 'checkout',
        status: 'failed',
        cartValue: total,
        errorMessage,
      });

      trackOrderEvent({
        action: 'placement',
        status: 'failed',
        errorMessage,
      });

      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          'Failed to process payment. Please try again.'
      );
      setTransactionState('idle');
    } finally {
      setLoading(false);
    }
  };

  const getButtonContent = () => {
    if (transactionState === 'processing') {
      return (
        <div className="flex items-center justify-center gap-[12px]">
          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Processing...
        </div>
      );
    }
    if (transactionState === 'success') {
      return (
        <div className="flex items-center justify-center gap-[12px]">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          Success
        </div>
      );
    }
    return `Pay UGX ${total.toLocaleString()}`;
  };

  return (
    <main className="bg-light-secondary dark:bg-brand-dark min-h-screen transition-colors duration-200">
      <div className="max-w-[600px] mx-auto px-[24px] flex items-center justify-center" style={{ minHeight: `${availableHeight}px` }}>
        <div className="w-full">
          <div className="text-center mb-[24px]">
            <h1 className="text-[28px] md:text-[32px] font-bold mb-[8px] text-light-text-primary dark:text-brand-text-primary">Payment</h1>
            <p className="text-[15px] text-light-text-secondary dark:text-brand-text-secondary">Choose your preferred payment method</p>
          </div>
          
          {error && (
            <div className="bg-[#FEE2E2] dark:bg-brand-error/20 border border-brand-error text-brand-error px-[16px] py-[12px] rounded-[12px] mb-[20px] text-[14px]">
              {error}
            </div>
          )}

          <div className="bg-white dark:bg-brand-surface p-[20px] rounded-[18px] border border-light-border-default dark:border-brand-border-default shadow-sm mb-[24px]">
            <span className="block text-[13px] font-medium text-light-text-secondary dark:text-brand-text-secondary mb-[6px]">Total Amount</span>
            <span className="text-[32px] font-extrabold block text-light-text-primary dark:text-brand-text-primary">
              UGX {total.toLocaleString()}
            </span>
          </div>
          
          <div className="space-y-[14px] mb-[24px]">
            <PaymentCard 
              title="Mobile Money" 
              description="MTN MoMo or Airtel Money"
              icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect><line x1="12" y1="18" x2="12.01" y2="18"></line></svg>}
              active={selectedMethod === 'mobile_money'}
              onClick={() => setSelectedMethod('mobile_money')}
            />
            
            <PaymentCard 
              title="Bank Transfer" 
              description="Direct transfer to our bank account"
              icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" ry="2"></rect><line x1="2" y1="10" x2="22" y2="10"></line></svg>}
              active={selectedMethod === 'bank_transfer'}
              onClick={() => setSelectedMethod('bank_transfer')}
            />
            
            <PaymentCard 
              title="Cash on Delivery" 
              description="Pay when you receive your order"
              icon={<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"></line><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path></svg>}
              active={selectedMethod === 'cash_on_delivery'}
              onClick={() => setSelectedMethod('cash_on_delivery')}
            />
          </div>
          
          <Button
            className={`
              w-full h-[56px] text-[17px] rounded-[12px] font-bold transition-all duration-300 flex items-center justify-center
              ${transactionState === 'success' ? 'bg-brand-success hover:bg-brand-success text-white' : 'bg-black text-white hover:bg-black/90'}
              disabled:opacity-80 disabled:cursor-wait
            `}
            disabled={loading || transactionState !== 'idle'}
            onClick={processPayment}
          >
            {getButtonContent()}
          </Button>
        </div>
      </div>
    </main>
  );
};

export default Payment;
